import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import IMask from 'imask';
import { Subscription } from 'rxjs';
import { Unit } from 'src/app/core/interfaces/unit';
import { FieldsAccessService } from 'src/app/core/services/fields-access/fields-access.service';
import { UnitsService } from 'src/app/core/services/units/units.service';
import { StringCanBeNumber } from 'src/app/shared/utils/string-can-be-number';

const INPUT_FIELD_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => InputFieldComponent),
  multi: true,
};
@Component({
  selector: 'app-input-field',
  templateUrl: './input-field.component.html',
  styleUrls: ['./input-field.component.scss'],
  providers: [INPUT_FIELD_VALUE_ACCESSOR],
})
export class InputFieldComponent
  implements ControlValueAccessor, OnChanges, AfterViewInit, OnDestroy
{
  @Input() label = '';
  @Input() id = '';
  @Input() name = '';
  @Input() placeholder = '';
  @Input() type = 'text';
  @Input() uppercase = false;
  @Input() disabled = false;
  @Input() darkErrors = false;
  @Input() readonly = false;
  @Input() highlighted = false;
  @Input() clickable = false;
  @Input() small = false;
  @Input() center = false;
  @Input() outsideIcon: string;
  @Input() insideIcon: string;
  @Input() mask: any;
  @Input() dateMask: any;
  @Input() maxlength: string;
  @Input() quantity: string;
  @Input() formName: string;
  @Input() title: string;

  @Output() outsideIconClick = new EventEmitter<void>();
  @Output() insideIconClick = new EventEmitter<void>();
  @Output() blurEv = new EventEmitter<any>(null);
  @Output() focusEv = new EventEmitter<any>(null);

  @ViewChild('fieldElement', { static: true, read: ElementRef })
  fieldElement: ElementRef;

  wrapperClass: object;
  maskRef: any;
  dropdownVisible: boolean;
  unitStr: string;
  innerVal: string | number;
  $globalAccess: Subscription;

  constructor(
    private renderer: Renderer2,
    private unitService: UnitsService,
    private fieldsAccessService: FieldsAccessService,
  ) {}

  ngOnChanges(): void {
    this.setWrapperClass();
  }

  ngAfterViewInit(): void {
    this.setMask();
    this.preserveGlobalAccess();
  }

  ngOnDestroy(): void {
    this.$globalAccess?.unsubscribe();
  }

  onChangeCb: (_?: any) => void = () => {};

  onTouchedCb: () => void = () => {};

  registerOnChange(fn: any): void {
    this.onChangeCb = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCb = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.setWrapperClass();
  }

  onChange(): void {
    setTimeout(() => {
      this.updateValue();
      this.onChangeCb(this.innerVal);
    });
  }

  onBlur(): void {
    this.maskRef?.updateValue();
    this.updateValue();
    this.onTouchedCb();
    this.blurEv.emit();
  }

  onFocus(): void {
    this.focusEv.emit();
  }

  updateValue(): void {
    this.value = this.getParsedValue(this.fieldValue);
  }

  getParsedValue(val: any): any {
    return this.isNumber(val) ? +val : val;
  }

  getValueOrEmptyStr(val: any): string | number {
    return val === null || val === undefined ? '' : val;
  }

  isNumber(val: any): boolean {
    return StringCanBeNumber(val);
  }

  writeValue(val: any): void {
    const fieldVal = this.getValueOrEmptyStr(val);
    this.renderer.setProperty(this.fieldElement.nativeElement, 'value', fieldVal);
    this.maskRef?.updateValue();
    this.onChange();
  }

  setMask(): void {
    if (this.mask?.date) {
      this.maskRef = IMask(this.fieldElement.nativeElement, {
        mask: Date,
        pattern: 'm/`d/`Y',
        blocks: {
          m: { mask: IMask.MaskedRange, from: 1, to: 12, maxLength: 2 },
          d: { mask: IMask.MaskedRange, from: 1, to: 31, maxLength: 2 },
          Y: { mask: IMask.MaskedRange, from: 1900, to: 99999 },
        },
      });
      return;
    }

    if (this.mask) {
      this.maskRef = IMask(this.fieldElement.nativeElement, this.mask);
      this.maskRef.on('accept', () => {
        this.maskRef?.updateValue();
        this.writeValue(this.maskRef.value);
      });
    }
  }

  setWrapperClass(): void {
    this.wrapperClass = {
      disabled: this.disabled,
      readonly: this.readonly,
      clickable: this.clickable,
      small: this.small,
      center: this.center,
      'dark-errors': this.darkErrors,
      'has-outside-icon': !!this.outsideIcon,
      'has-inside-icon': !!this.insideIcon,
    };
  }

  preserveGlobalAccess(): void {
    this.$globalAccess = this.fieldsAccessService.canEdit.subscribe((canEdit) => {
      this.readonly = canEdit ? this.readonly : true;
      this.disabled = canEdit ? this.disabled : true;
    });
  }

  setFieldUnit(unit: Unit): void {
    if (this.fieldValue && this.unitStr) {
      const newValue = this.unitService
        .transform(
          this.formName,
          this.id,
          this.quantity,
          unit.reference,
          +this.fieldValue,
          true,
          !this.unitStr,
        )
        .toString();
      if (newValue !== this.fieldValue) {
        this.writeValue(newValue);
      }
    }

    this.unitStr = unit.value;
  }

  getLabel(): string {
    let label = this.label;
    if (this.uppercase) {
      label = this.label.toUpperCase();
    }
    if (this.quantity) {
      label = `${label} (${this.unitStr})`;
    }
    return label;
  }

  emitOutsideIconClick(): void {
    this.outsideIconClick.emit();
  }

  emitInsideIconClick(): void {
    this.insideIconClick.emit();
  }

  set value(v: any) {
    this.innerVal = v;
  }

  get fieldValue(): number | string {
    return this.maskRef ? this.maskRef.value : this.fieldElement.nativeElement.value;
  }
}
